import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000/api';

const client = axios.create({
  baseURL: `${API_BASE_URL}/full-time`,
  withCredentials: true,
});

function errMsg(error: any, fallback: string) {
  return error.response?.data?.message || error.response?.data?.error || fallback;
}

export interface FullTimeTrack {
  id: string;
  employeeEmail: string;
  teamId?: number;
  startedAt: string;
  endedAt?: string | null;
  completedByEmail: string;
  status: 'ACTIVE' | 'CLOSED';
  reviewerEmail?: string;
}

export interface EligibilityData {
  eligible: boolean;
  employeeEmail: string;
  teamRole: string | null;
  teamId: number | null;
  hasCompletedDemo: boolean;
  completedDemoCount: number;
  joinerTrackStatus: string | null;
  hasFullTimeTrack: boolean;
  fullTimeTrackStatus: string | null;
  reason?: string;
}

export interface PerformanceMeeting {
  id: string;
  title: string;
  date: string;
  time: string;
  duration: number;
  meetingType: 'ONE_ON_ONE' | 'PERFORMANCE' | 'GOAL_REVIEW';
  status: 'scheduled' | 'conducted' | 'completed' | 'cancelled';
  employeeEmail: string;
  teamId?: number;
  createdBy: string;
  attendees?: string[];
  notes?: string;
  calendarEventId?: string;
  year?: number | null;
  quarter?: number | null;
  completedAt?: string | null;
  goalsOpenUntil?: string | null;
  autoScheduled?: boolean;
}

export interface PerformanceFeedback {
  id: string;
  meetingId: string;
  employeeEmail: string;
  reviewerEmail: string;
  reviewerRole: 'TEAM_LEAD' | 'HR' | 'ADMIN';
  overallRating?: number | null;
  overallReviewContent: {
    /** Retained only to render feedback created before meeting-level ratings were removed. */
    detailedRatings?: { category: string; rating: number; comments?: string }[];
    overallFeedback?: string;
    suggestionsForImprovement?: string[];
  };
  status: 'PENDING' | 'COMPLETED';
  reviewedAt?: string;
}

export interface PerformanceGoal {
  id: string;
  employeeEmail: string;
  meetingId?: string;
  title: string;
  description?: string;
  targetDate?: string;
  status: 'OPEN' | 'IN_PROGRESS' | 'ACHIEVED' | 'DROPPED';
  reviewStatus?: 'PENDING_REVIEW' | 'APPROVED' | 'CHANGES_REQUESTED';
  reviewerNotes?: string | null;
  reviewRating?: number | null;
  progressNotes?: string;
  createdByEmail: string;
}

export interface FullTimeMemberRow {
  track: FullTimeTrack;
  employee: { email: string; name: string };
  openGoalsCount: number;
  nextMeeting: PerformanceMeeting | null;
  teamName?: string;
  /** True when track is ACTIVE and employee.is_active is not false */
  isActive?: boolean;
  employeeActive?: boolean;
}

export interface PerformanceDashboardData {
  track: FullTimeTrack | null;
  upcomingMeeting: PerformanceMeeting | null;
  latestFeedback: PerformanceFeedback | null;
  goals: PerformanceGoal[];
  latestCompletedMeeting: PerformanceMeeting | null;
  openMeetingForGoals: PerformanceMeeting | null;
  canAddGoals?: boolean;
  goalsWindowOpenUntil?: string | null;
  goalsWindowClosed?: boolean;
  pendingGoalReviews?: number;
}

const fullTimeAPI = {
  getMe: async (): Promise<{ hasActiveTrack: boolean; track: FullTimeTrack | null }> => {
    try {
      const res = await client.get('/me');
      return res.data.data;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load full-time track'));
    }
  },

  getEligibility: async (email: string): Promise<EligibilityData> => {
    try {
      const res = await client.get(`/eligibility/${encodeURIComponent(email)}`);
      return res.data.data;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to check eligibility'));
    }
  },

  completeOnboarding: async (employeeEmail: string) => {
    try {
      const res = await client.post('/complete-onboarding', { employeeEmail });
      return res.data;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to complete onboarding'));
    }
  },

  getMembers: async (teamId?: number): Promise<FullTimeMemberRow[]> => {
    try {
      const params = teamId ? `?teamId=${teamId}` : '';
      const res = await client.get(`/members${params}`);
      return res.data.data;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load full-time members'));
    }
  },

  getDashboard: async (employeeEmail?: string): Promise<PerformanceDashboardData> => {
    try {
      const params = employeeEmail
        ? `?employeeEmail=${encodeURIComponent(employeeEmail)}`
        : '';
      const res = await client.get(`/dashboard${params}`);
      return res.data.data as PerformanceDashboardData;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load performance dashboard'));
    }
  },

  listMeetings: async (filters?: {
    employeeEmail?: string;
    status?: string;
    year?: number;
    quarter?: number;
    autoScheduled?: boolean;
  }) => {
    try {
      const params = new URLSearchParams();
      if (filters?.employeeEmail) params.append('employeeEmail', filters.employeeEmail);
      if (filters?.status) params.append('status', filters.status);
      if (filters?.year != null) params.append('year', String(filters.year));
      if (filters?.quarter != null) params.append('quarter', String(filters.quarter));
      if (filters?.autoScheduled !== undefined) {
        params.append('autoScheduled', String(filters.autoScheduled));
      }
      const q = params.toString() ? `?${params}` : '';
      const res = await client.get(`/meetings${q}`);
      return res.data.data as PerformanceMeeting[];
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load meetings'));
    }
  },

  createMeeting: async (data: Partial<PerformanceMeeting> & { title: string; date: string; time: string; employeeEmail: string }) => {
    try {
      const res = await client.post('/meetings', data);
      return res.data.data as PerformanceMeeting;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to create meeting'));
    }
  },

  getMeeting: async (id: string) => {
    try {
      const res = await client.get(`/meetings/${id}`);
      return res.data.data as {
        meeting: PerformanceMeeting;
        feedbacks: PerformanceFeedback[];
        goals: PerformanceGoal[];
        ratingCategories: string[];
        canAddGoals?: boolean;
        goalsWindowOpenUntil?: string | null;
        goalsWindowClosed?: boolean;
        pendingGoalReviews?: number;
      };
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load meeting'));
    }
  },

  reopenGoalsWindow: async (meetingId: string) => {
    try {
      const res = await client.post(`/meetings/${meetingId}/reopen-goals`);
      return res.data as {
        success: boolean;
        data: PerformanceMeeting;
        canAddGoals?: boolean;
        goalsWindowOpenUntil?: string | null;
      };
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to reopen goals window'));
    }
  },

  updateMeeting: async (id: string, data: Partial<PerformanceMeeting>) => {
    try {
      const res = await client.patch(`/meetings/${id}`, data);
      return res.data.data as PerformanceMeeting;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to update meeting'));
    }
  },

  /** Soft-delete: mark meeting as cancelled */
  cancelMeeting: async (id: string) => {
    try {
      const res = await client.patch(`/meetings/${id}`, { status: 'cancelled' });
      return res.data.data as PerformanceMeeting;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to cancel meeting'));
    }
  },

  submitFeedback: async (
    meetingId: string,
    data: {
      overallReviewContent: PerformanceFeedback['overallReviewContent'];
      overallRating?: number;
      status?: 'PENDING' | 'COMPLETED';
      goals?: Partial<PerformanceGoal>[];
    }
  ) => {
    try {
      const res = await client.post(`/meetings/${meetingId}/feedback`, data);
      return res.data.data;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to submit feedback'));
    }
  },

  listGoals: async (employeeEmail?: string) => {
    try {
      const params = employeeEmail
        ? `?employeeEmail=${encodeURIComponent(employeeEmail)}`
        : '';
      const res = await client.get(`/goals${params}`);
      return res.data.data as PerformanceGoal[];
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to load goals'));
    }
  },

  createGoal: async (data: {
    employeeEmail: string;
    title: string;
    description?: string;
    targetDate?: string;
    meetingId?: string;
    status?: string;
    reviewStatus?: PerformanceGoal['reviewStatus'];
  }) => {
    try {
      const res = await client.post('/goals', data);
      return res.data.data as PerformanceGoal;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to create goal'));
    }
  },

  updateGoal: async (
    id: string,
    data: Partial<PerformanceGoal> & { appendProgressNote?: string }
  ) => {
    try {
      const res = await client.patch(`/goals/${id}`, data);
      return res.data.data as PerformanceGoal;
    } catch (error: any) {
      throw new Error(errMsg(error, 'Failed to update goal'));
    }
  },
};

export default fullTimeAPI;
